import classNames from 'classnames';

import React, {
  forwardRef,
  ForwardRefExoticComponent,
  HTMLAttributes,
  RefAttributes,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Badge } from '../badge';
import { Icon } from '../icon';
import { Typography } from '../typography';

import styles from './collection.scss';
import { DEFAULT_PATTERN, generateGrid, getColumnsCount } from './lib';
import {
  Grid as GridType,
  ItemSize,
  ItemType,
  Link,
  RenderItemBadges,
  RenderItemWrapper,
} from './types';

const GRID_GAP = 8;

const COMPONENT_ID = 'modulor-collection';

const HEIGHT_MAP: Record<ItemSize, string> = {
  1: '56px',
  2: '120px',
  3: '184px',
  4: '248px',
  5: '312px',
};

const handleClick = (e: React.MouseEvent, target: ItemType | Link) => {
  if (typeof target.onClick === 'function') {
    e.preventDefault();
    const { onClick, ...eventTarget } = target;
    onClick(e, eventTarget);
  }
};

type BottomLinkProps = {
  bottomLink: Link;
  dataTestId?: string;
};

const BottomLink: React.FC<BottomLinkProps> = ({ bottomLink, dataTestId }) => {
  return (
    <a
      className={styles['bottom-link']}
      href={bottomLink.url}
      data-testid={dataTestId}
      onClick={(e) => handleClick(e, bottomLink)}>
      <Typography variant="subhead-semibold">{bottomLink.title}</Typography>
      <Icon name="forward-android" fill="text-title" />
    </a>
  );
};

type ItemProps = {
  item: ItemType;
  size: ItemSize;
  itemTitleVariant?: string;
  renderItemBadges?: RenderItemBadges;
  imageFallback?: React.ReactNode;
  dataTestId?: string;
};

const Item: React.FC<ItemProps> = ({
  item,
  size,
  itemTitleVariant = 'title-2-semibold',
  renderItemBadges,
  imageFallback,
  dataTestId,
}) => {
  const [isImageLoadingError, setIsImageLoadingError] = useState(false);
  const { label, url, image, title } = item;
  const height = HEIGHT_MAP[size];

  const onImageLoadingError = () => {
    setIsImageLoadingError(true);
  };

  return (
    <a
      href={url}
      className={styles.item}
      style={{ height: height }}
      data-testid={dataTestId}
      onClick={(e) => handleClick(e, item)}>
      <>
        {isImageLoadingError ? (
          imageFallback
        ) : (
          <img
            className={styles.image}
            src={image}
            onError={imageFallback ? onImageLoadingError : null}
            alt={title}
            loading="lazy"
            data-testid={`${COMPONENT_ID}-image`}
          />
        )}
        {renderItemBadges && renderItemBadges(item)}
        {label && <Badge text={label} dataTestId={`${dataTestId}-badge`} />}
        <Typography
          className={styles.title}
          variant={itemTitleVariant}
          dataTestId={`${COMPONENT_ID}-title`}>
          {title}
        </Typography>
        <div
          className={styles['play-button-icon']}
          data-id="play-button-icon"
          data-test-id="play-button-icon">
          <Icon size={36} name="play_filled" fill="icon-primary-control" />
        </div>
      </>
    </a>
  );
};

type ColumnProps = {
  column: ItemSize[];
  index: number;
  columnWidth: string;
  bottomLink: Link;
  items: ItemType[];
  columnsCount: number;
  itemTitleVariant?: string;
  renderItemBadges?: RenderItemBadges;
  renderItemWrapper?: RenderItemWrapper;
  imageFallback?: React.ReactNode;
  dataTestId?: string;
};

const Column: React.FC<ColumnProps> = ({
  column,
  index,
  columnWidth,
  bottomLink,
  items,
  columnsCount,
  itemTitleVariant,
  renderItemBadges,
  renderItemWrapper,
  imageFallback,
  dataTestId,
}) => {
  return (
    <div className={styles.column} style={{ width: `${columnWidth}` }} data-testid={dataTestId}>
      {column.map((size, sizeIndex) => {
        const itemIndex: number = sizeIndex * columnsCount + index;
        const item = items[itemIndex];

        const itemComponent = (
          <Item
            key={item?.title}
            item={item}
            size={size}
            renderItemBadges={renderItemBadges}
            imageFallback={imageFallback}
            itemTitleVariant={itemTitleVariant}
            dataTestId={`${COMPONENT_ID}-item`}
          />
        );

        return size === 1 && bottomLink != null ? (
          <BottomLink
            key={bottomLink.title}
            bottomLink={bottomLink}
            dataTestId={`${COMPONENT_ID}-bottom-link`}
          />
        ) : renderItemWrapper ? (
          renderItemWrapper(item, itemComponent)
        ) : (
          itemComponent
        );
      })}
    </div>
  );
};

type GridProps = {
  grid: GridType;
  onClick?: React.MouseEventHandler;
  columnWidth: string;
  bottomLink: Link;
  items: ItemType[];
  columnsCount: number;
  itemTitleVariant?: string;
  renderItemBadges?: RenderItemBadges;
  renderItemWrapper?: RenderItemWrapper;
  imageFallback?: React.ReactNode;
  dataTestId?: string;
};

const Grid: React.FC<GridProps> = ({
  grid,
  onClick,
  columnWidth,
  bottomLink,
  items,
  columnsCount,
  itemTitleVariant,
  renderItemBadges,
  renderItemWrapper,
  imageFallback,
  dataTestId,
}) => {
  return (
    <div onClick={onClick} className={styles.grid} data-testid={dataTestId}>
      {grid.map((column, index) => (
        <Column
          key={index}
          column={column}
          index={index}
          columnWidth={columnWidth}
          bottomLink={bottomLink}
          items={items}
          dataTestId={`${COMPONENT_ID}-column`}
          columnsCount={columnsCount}
          itemTitleVariant={itemTitleVariant}
          renderItemBadges={renderItemBadges}
          renderItemWrapper={renderItemWrapper}
          imageFallback={imageFallback}
        />
      ))}
    </div>
  );
};

type Props = RefAttributes<HTMLDivElement> &
  HTMLAttributes<HTMLDivElement> & {
    items: ItemType[];
    pattern?: ItemSize[];
    bottomLink: Link;
    itemTitleVariant?: string;
    renderItemBadges?: RenderItemBadges;
    imageFallback?: React.ReactNode;
    dataTestId?: string;
    isCustomMobileView?: boolean;
    columnsCount?: number;
    columnSize?: number;
    renderItemWrapper?: RenderItemWrapper;
  };

export const Collection: ForwardRefExoticComponent<Props> = forwardRef<HTMLDivElement, Props>(
  (
    {
      items: data = [],
      pattern = DEFAULT_PATTERN,
      bottomLink,
      onClick,
      itemTitleVariant,
      renderItemBadges,
      imageFallback,
      dataTestId = COMPONENT_ID,
      className,
      isCustomMobileView,
      columnsCount: customColumnsCount,
      columnSize,
      renderItemWrapper,
      ...rest
    },
    ref,
  ) => {
    const [columnsCount, setColumnsCount] = useState<number>(
      () => customColumnsCount || getColumnsCount(isCustomMobileView),
    );

    useEffect(() => {
      if (customColumnsCount) {
        setColumnsCount(customColumnsCount);
      }
    }, [customColumnsCount]);

    const items = useMemo(() => {
      return columnSize ? data.slice(0, columnSize * columnsCount) : data;
    }, [columnsCount, columnSize, data]);

    const grid = useMemo(() => {
      const withLinkExtraSpace = bottomLink != null;
      return generateGrid(items.length, columnsCount, {
        withLinkExtraSpace,
        pattern: pattern,
      });
    }, [items, columnsCount, bottomLink, pattern]);

    const handleViewportChange = () => setColumnsCount(getColumnsCount(isCustomMobileView));

    const columnWidth = (): string => {
      return `calc(${100 / columnsCount}% - ${GRID_GAP * (columnsCount - 1)}px)`;
    };

    useEffect(() => {
      if (!customColumnsCount) {
        window.addEventListener('resize', handleViewportChange);

        return () => window.removeEventListener('resize', handleViewportChange);
      }
    }, [customColumnsCount]);

    return (
      <div
        data-testid={dataTestId}
        data-id={dataTestId}
        data-component-name={COMPONENT_ID}
        className={classNames(className, styles.collection)}
        ref={ref}
        {...rest}>
        <Grid
          bottomLink={bottomLink}
          items={items}
          columnsCount={columnsCount}
          grid={grid}
          onClick={onClick}
          columnWidth={columnWidth()}
          itemTitleVariant={itemTitleVariant}
          renderItemBadges={renderItemBadges}
          renderItemWrapper={renderItemWrapper}
          imageFallback={imageFallback}
          dataTestId={`${dataTestId}-grid`}
        />
      </div>
    );
  },
);

Collection.displayName = 'Collection';
